// Como um evento do handoff vira frase na tela.
//
// O rastro é honesto e ilegível: `bot_silenciado` por `humano_respondeu`, `resolve_ignorado`,
// `espelho_reidratado`. Quem atende não deve precisar aprender esse vocabulário para
// responder "por que o bot parou de falar com essa pessoa?".
//
// Fica em helper e não no componente pelo mesmo motivo do followupTrace: o mapa é lido pelo
// painel da conversa e pelo botão do cabeçalho, e um segundo mapa divergiria no primeiro dia.

// A chave é o EVENTO, exatamente como o runtime o grava (`conversation_events.evento`). Um
// evento novo aparece cru: feio e verdadeiro, e é o que faz alguém perceber que falta traduzir.
const FRASES = {
  handoff_solicitado: 'Atendimento humano solicitado',
  bot_silenciado: 'Bot pausado',
  bot_retomado: 'Bot retomado',
  resolve_ignorado: 'Conversa resolvida, e o bot seguiu como estava',
  handoff_escalado: 'Pedido de humano escalado para outro time',
  espelho_reidratado: 'Estado do bot reconciliado com o runtime',
};

// A origem completa a frase: "Bot pausado" sozinho não responde a pergunta. Só as origens
// conhecidas entram — o runtime também grava o motivo da decisão nessa coluna (`resolve`,
// nomes de regra), e colar isso cru no fim da frase vira "Bot retomado resolvida".
const POR_ORIGEM = {
  humano_respondeu: 'porque um atendente respondeu',
  tool: 'pelo próprio agente',
  botao: 'pela tela',
  api: 'por uma integração',
  dormencia: 'por dormência',
  bloqueio: 'por um bloqueio',
  resolve: 'porque a conversa foi resolvida',
  // PERF-01 F2: o turno desistiu e a contingência passou a conversa para a equipe.
  contingencia: 'porque o agente não conseguiu responder',
};

// Eventos em que a origem já está na frase — repeti-la diria "porque a conversa foi
// resolvida" duas vezes.
const SEM_ORIGEM = new Set(['resolve_ignorado', 'espelho_reidratado']);

// Aceita a linha do runtime (`{ evento, origem, ... }`) ou só o nome do evento.
export function fraseDoEvento(evento) {
  const { evento: nome, origem } =
    typeof evento === 'string' ? { evento } : evento || {};
  const frase = FRASES[nome];
  if (!frase) return nome || '';
  const complemento = SEM_ORIGEM.has(nome) ? '' : POR_ORIGEM[origem];
  return complemento ? `${frase} ${complemento}` : frase;
}

// Quatro estados e não dois. Pausa e retomada são o que muda o que o cliente recebe; o
// pedido de humano pode ou não calar o bot (é configuração); e o resto é registro — a
// conversa resolvida que não religou, o espelho corrigido — que a tela mostra sem cor.
const ESTADOS = {
  bot_silenciado: 'pausa',
  bot_retomado: 'retomada',
  handoff_solicitado: 'pedido',
  handoff_escalado: 'pedido',
};

export function estadoDoEvento(evento) {
  const nome = typeof evento === 'string' ? evento : evento?.evento;
  return ESTADOS[nome] || 'neutro';
}

// As causas que o runtime grava em `silenciado_por` (migração 016, mais `contingencia` na
// 026), como sufixo de chave i18n (`CONVERSATION.AGENT_BOT.PAUSED_BY.*`). Fora delas é
// `UNKNOWN`, e não a causa crua: o texto vai para o cliente do produto, não para quem lê o
// banco. Cada uma precisa da sua entrada em `useAgentBot` (CAUSAS) e no i18n.
const ORIGENS_CONHECIDAS = new Set([
  'humano_respondeu',
  'tool',
  'botao',
  'api',
  'dormencia',
  'bloqueio',
  'contingencia',
]);

export function chaveDaOrigem(origem) {
  return ORIGENS_CONHECIDAS.has(origem) ? origem.toUpperCase() : 'UNKNOWN';
}

// O espelho `additional_attributes.bot` depois da resposta do `PUT .../bot`.
//
// Sem update otimista, de propósito: o escritor do espelho é o runtime (PLAN §1), e o que a
// tela aplica é a linha LIDA DE VOLTA que ele respondeu — o realtime traz a mesma coisa em
// seguida. Merge raso em cima do que já havia: `handoff_solicitado_em` e `atualizado_em`,
// que o PUT não devolve nesse formato, ficam como estavam.
export function espelhoDoBot(attrs, resposta) {
  const atuais = attrs || {};
  return {
    ...atuais,
    bot: {
      ...(atuais.bot || {}),
      active: resposta.bot_active,
      silenciado_por: resposta.silenciado_por,
    },
  };
}
